import { useEffect, useState } from 'react';

import { Loader2 } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Input } from '../../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { cn } from '../../../../lib/utils';
import { getFileExtension } from '../../lib/mediaEntries';
import {
  applyPresetPrefix,
  formatPrefixLabel,
  getPresetPrefix,
  PRESET_PREFIXES,
  type PresetPrefix,
} from '../../lib/presetPrefixes';

export type RenamePayload = { kind: 'prefix'; prefix: PresetPrefix } | { kind: 'name'; newName: string };

interface RenamePrefixDialogProps {
  allowPrefix?: boolean;
  fileNames: string[];
  onCancel: () => void;
  onConfirm: (payload: RenamePayload) => Promise<void>;
  open: boolean;
}

type Mode = 'prefix' | 'name';

const getBaseName = (fileName: string): string => {
  const ext = getFileExtension(fileName);
  return ext ? fileName.slice(0, -(ext.length + 1)) : fileName;
};

const RenamePrefixDialog = ({ allowPrefix = true, fileNames, onCancel, onConfirm, open }: RenamePrefixDialogProps) => {
  const isSingle = fileNames.length === 1;

  const [mode, setMode] = useState<Mode>('prefix');
  const [selectedPrefix, setSelectedPrefix] = useState<PresetPrefix | ''>('');
  const [nameBase, setNameBase] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const detected = fileNames.length > 0 ? getPresetPrefix(fileNames[0]) : null;
    setSelectedPrefix(detected ?? '');
    setNameBase(fileNames.length > 0 ? getBaseName(fileNames[0]) : '');
    setMode(allowPrefix ? 'prefix' : 'name');
    setIsLoading(false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const extension = isSingle && fileNames[0] ? `.${getFileExtension(fileNames[0])}` : '';
  const initialBase = isSingle && fileNames[0] ? getBaseName(fileNames[0]) : '';

  const isNameMode = !allowPrefix || (isSingle && mode === 'name');

  const isConfirmDisabled =
    isLoading || (isNameMode ? nameBase.trim() === '' || nameBase === initialBase : !selectedPrefix);

  const handleConfirm = async () => {
    if (isConfirmDisabled) return;
    setIsLoading(true);
    try {
      if (isNameMode) {
        await onConfirm({ kind: 'name', newName: `${nameBase}${extension}` });
      } else {
        await onConfirm({ kind: 'prefix', prefix: selectedPrefix as PresetPrefix });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const previews =
    !isNameMode && selectedPrefix ? fileNames.slice(0, 3).map((name) => applyPresetPrefix(name, selectedPrefix)) : [];

  const fileCountLabel = `${fileNames.length} ${fileNames.length === 1 ? 'archivo' : 'archivos'}`;

  const title = isNameMode ? 'Editar nombre del archivo' : 'Editar prefijo de título';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !isLoading) onCancel();
      }}
    >
      <DialogContent
        className={cn(
          'max-h-[calc(100dvh-(--spacing(4)))] w-[calc(100%-1rem)] max-w-[calc(100%-1rem)]',
          'overflow-y-auto sm:max-w-sm'
        )}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {isSingle && allowPrefix && (
            <div className="flex gap-1 rounded-lg border border-input p-0.5">
              {(['prefix', 'name'] as const).map((m) => (
                <button
                  className={cn(
                    'flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    mode === m ? 'bg-white text-foreground shadow-sm' : 'text-text-muted hover:text-foreground'
                  )}
                  disabled={isLoading}
                  key={m}
                  onClick={() => setMode(m)}
                  type="button"
                >
                  {m === 'prefix' ? 'Por prefijo' : 'Nombre completo'}
                </button>
              ))}
            </div>
          )}

          {isNameMode ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-bold uppercase tracking-wide text-text-subtle" id="rename-name-label">
                Nuevo nombre
              </span>
              <div className="flex min-w-0 items-center gap-1">
                <Input
                  aria-labelledby="rename-name-label"
                  className="min-w-0 flex-1"
                  disabled={isLoading}
                  onChange={(e) => setNameBase(e.target.value)}
                  value={nameBase}
                />
                {extension && (
                  <span
                    className={cn(
                      'max-w-[40%] shrink-0 truncate rounded bg-neutral-100 px-2 py-1.5',
                      'font-mono text-xs text-text-muted'
                    )}
                  >
                    {extension}
                  </span>
                )}
              </div>
              <p className="text-xs text-text-muted">La extensión se mantiene.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <label
                className="text-xs font-bold uppercase tracking-wide text-text-subtle"
                htmlFor="rename-prefix-select"
              >
                {`Nuevo prefijo (${fileCountLabel})`}
              </label>
              <Select
                disabled={isLoading}
                onValueChange={(v) => setSelectedPrefix(v as PresetPrefix)}
                value={selectedPrefix}
              >
                <SelectTrigger id="rename-prefix-select">
                  <SelectValue placeholder="Selecciona un prefijo…" />
                </SelectTrigger>
                <SelectContent>
                  {PRESET_PREFIXES.map((prefix) => (
                    <SelectItem key={prefix} value={prefix}>
                      {formatPrefixLabel(prefix)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {previews.length > 0 && (
            <div className="flex flex-col gap-1">
              <p className="text-xs font-bold uppercase tracking-wide text-text-subtle">Previsualización</p>
              {previews.map((name) => (
                <p key={name} className="truncate rounded bg-neutral-50 px-3 py-1.5 font-mono text-xs text-text-muted">
                  {name}
                </p>
              ))}
              {fileNames.length > 3 && <p className="text-xs text-text-muted">{`…y ${fileNames.length - 3} más`}</p>}
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button disabled={isLoading} onClick={onCancel} type="button" variant="outline">
              Cancelar
            </Button>
          </DialogClose>
          <Button
            className="bg-brand-vivid text-white hover:bg-brand-sky-dark"
            disabled={isConfirmDisabled}
            onClick={() => {
              void handleConfirm();
            }}
            type="button"
          >
            {isLoading && <Loader2 className="size-4 animate-spin" />}
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RenamePrefixDialog;
